import { Card } from "@/components/ui/card";

const KIND_COLORS = {
  bedroom: "#1e3a8a",
  bath:    "#0e7490",
  kitchen: "#92400e",
  living:  "#374151",
  corridor:"#1f2937",
  entry:   "#4c1d95",
  closet:  "#0f172a",
  sleep:   "#1e3a8a",
};

export default function LegendPanel({ building, summary, activeFloor }) {
  const byType = summary?.by_type || {};

  // Count units across all wings at the active floor level + per-type breakdown
  let unitsOnFloor = 0;
  const floorByType = { STUDIO_15: 0, ONEBR_25: 0, TWOBR_40: 0, COMM_40: 0 };
  (building.wings || []).forEach((w) => {
    const f = (w.floors || []).find((fl) => fl.level === activeFloor);
    if (!f) return;
    unitsOnFloor += f.units?.length || 0;
    (f.units || []).forEach((u) => {
      if (floorByType[u.unit_type_id] !== undefined) floorByType[u.unit_type_id] += 1;
    });
  });
  const twoBrPct = unitsOnFloor > 0
    ? Math.round((floorByType.TWOBR_40 / unitsOnFloor) * 100)
    : 0;

  return (
    <>
      <Card data-testid="unit-legend" className="bg-[#0f172a] border-[#1f2937] p-4">
        <div className="text-[10px] tracking-[0.3em] text-[#5b6776] mb-3">UNIT TYPES</div>
        <div className="space-y-3">
          {building.unit_types.map((u) => (
            <div data-testid={`legend-unit-${u.id}`} key={u.id} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-sm" style={{ background: u.color }} />
                <div>
                  <div className="text-sm text-[#e6edf3]">{u.label}</div>
                  <div className="text-[10px] text-[#94a3b8]">{u.length_ft}' × {u.depth_ft}' container</div>
                </div>
              </div>
              <span className="text-xs text-amber-300">×{byType[u.id] || 0}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="wing-legend" className="bg-[#0f172a] border-[#1f2937] p-4">
        <div className="text-[10px] tracking-[0.3em] text-[#5b6776] mb-3">WINGS</div>
        <div className="space-y-2 text-xs text-[#cbd5e1]">
          {(building.wings || []).map((w) => (
            <div key={w.id} className="flex items-center justify-between">
              <span>{w.label}</span>
              <span className="text-amber-300 font-mono">{Math.round(w.length_ft)}'</span>
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="room-legend" className="bg-[#0f172a] border-[#1f2937] p-4">
        <div className="text-[10px] tracking-[0.3em] text-[#5b6776] mb-3">ROOM KEY</div>
        <div className="grid grid-cols-2 gap-y-2 text-xs text-[#cbd5e1]">
          {Object.entries(KIND_COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 capitalize">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: v }} />
              {k}
            </div>
          ))}
        </div>
      </Card>

      <Card data-testid="egress-legend" className="bg-[#0f172a] border-[#1f2937] p-4">
        <div className="text-[10px] tracking-[0.3em] text-[#5b6776] mb-3">EGRESS &amp; SAFETY</div>
        <ul className="text-xs space-y-2 text-[#cbd5e1]">
          <li className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: "#facc15" }} /> Exterior steel stairs (end of each wing)</li>
          <li className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: "#22c55e" }} /> Unit entry (from catwalk)</li>
          <li className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm border-2 border-red-500" /> IRC R310 egress window</li>
          <li className="flex items-center gap-2"><span className="w-3 h-1 rounded-sm" style={{ background: "#ef4444" }} /> Egress travel path</li>
        </ul>
      </Card>

      <Card className="bg-[#0f172a] border-[#1f2937] p-4">
        <div className="text-[10px] tracking-[0.3em] text-[#5b6776] mb-2">VIEWING</div>
        <div data-testid="viewing-level-line" className="text-sm text-[#e6edf3]">Level {activeFloor} — {unitsOnFloor} units</div>
        {unitsOnFloor > 0 && (
          <div data-testid="floor-mix-line" className="text-[11px] text-[#cbd5e1] mt-2 leading-relaxed font-mono">
            <span className="text-emerald-400">{floorByType.STUDIO_15}S</span>
            <span className="text-[#5b6776]"> · </span>
            <span className="text-blue-400">{floorByType.ONEBR_25} × 1BR</span>
            <span className="text-[#5b6776]"> · </span>
            <span className="text-amber-300 font-bold">{floorByType.TWOBR_40} × 2BR</span>
            {floorByType.COMM_40 > 0 && (
              <>
                <span className="text-[#5b6776]"> · </span>
                <span className="text-orange-400">{floorByType.COMM_40} × COMM</span>
              </>
            )}
            <div className="text-amber-300/70 mt-1">2BR mix this floor: <span className="font-bold text-amber-300">{twoBrPct}%</span></div>
          </div>
        )}
        <div className="text-[10px] text-[#94a3b8] mt-2 leading-relaxed">
          Drag to orbit · Scroll to zoom · Right-drag to pan. X-RAY reveals interior partitions; BALCONY toggles courtyard-side balconies; SOLAR overlays roof PV.
        </div>
      </Card>
    </>
  );
}
